import React, { useEffect, useState, useMemo } from "react";
import { useStore } from "../../store/store";
import { useAccordion } from "../../store/accordion";

function isBtnDisabled(
  errorArray: (string | undefined)[],
  requiredArray: (string | undefined)[],
) {
  const errorArrayHasValue = errorArray.some((err) => Boolean(err) === true);

  if (errorArrayHasValue) return true;

  var withErr = requiredArray.every((addon) => addon) ? false : true;

  return withErr;
}

function useBtnSelection() {
  const editBtn = useStore((state) => state.isEdit);

  const handleMinimize = useAccordion((state) => state.handleMinimize);
  const handleError = useStore((state) => state.handleError);

  const errors = useStore((state) => state.errors);

  const doorType = useStore((state) => state.quote.doorType);
  const cornerType = useStore((state) => state.quote.cornerType);
  const cornerConfigRight = useStore((state) => state.quote.cornerConfigRight);
  const rightWidth = useStore((state) => state.quote.cornerWidthRight);
  const comboWindowConfig = useStore((state) => state.quote.comboWindowConfig);
  const comboWindowUnitSize = useStore(
    (state) => state.quote.comboWindowUnitSize,
  );
  const material = useStore((state) => state.quote.material);
  const unitSize = useStore((state) => state.quote.unitSize);
  const height = useStore((state) => state.quote.height);
  const exactConfig = useStore((state) => state.quote.exactConfig);
  const woodSpecies = useStore((state) => state.quote.woodSpecies);
  const swingPanelWidth = useStore((state) => state.quote.swingPanelWidth);
  const swingDirection = useStore((state) => state.quote.swingDirection);
  const configuration = useStore((state) => state.quote.configuration);
  const pivotHardware = useStore((state) => state.quote.pivotHardware);
  const pivotAction = useStore((state) => state.quote.pivotAction);
  const stilesAndRails = useStore((state) => state.quote.stilesAndRails);

  const [showPrevBtn, setShowPrevBtn] = useState(false);
  const [openPreview, setOpenPreview] = useState(false);
  const [showPortal, setShowPortal] = useState(false);

  const isAluminum = material === "All Aluminum";

  const isCorner = doorType?.includes("Corner");
  const isCombo = doorType?.includes("Combo");
  const isSlider = doorType === "Slider";
  const isSwing = doorType === "Swing";
  const isPivot = doorType === "Pivot";

  useEffect(() => {
    if (isSlider) return;
    const errKey = "configErr";

    if (isSwing) {
      if (swingDirection && !exactConfig) {
        return handleError(errKey, "Select a Configuration");
      }
      return handleError(errKey, "");
    }

    if (isCorner) {
      if (!cornerType) {
        return handleError(errKey, "Select a Corner Type");
      }

      if (!exactConfig || !cornerConfigRight) {
        return handleError(errKey, "Select a Configuration");
      }
    } else {
      if (configuration && !exactConfig) {
        return handleError(errKey, "Select a Configuration");
      }
    }

    handleError(errKey, "");
  }, [
    doorType,
    swingDirection,
    configuration,
    exactConfig,
    cornerType,
    cornerConfigRight,
  ]);

  const errorArray = isSwing
    ? [
        errors.widthErr,
        errors.heightErr,
        errors.jambErr,
        errors.configErr,
        errors.swingWidthErr,
        errors.sideliteWidthErr,
        errors.primaryDoorErr,
      ]
    : isCorner
      ? [
          errors.widthErr,
          errors.heightErr,
          errors.swingWidthErr,
          errors.jambErr,
          errors.configErr,
          errors.primaryDoorErr,
          errors.widthErrCornerRight,
        ]
      : isCombo
        ? [
            errors.widthErr,
            errors.widthErrComboWindow,
            errors.heightErr,
            errors.heightErrComboWindow,
            errors.jambErr,
            errors.configErr,
            errors.primaryDoorErr,
          ]
        : isPivot
          ? [
              errors.pivotPointErr,
              errors.widthErr,
              errors.heightErr,
              errors.jambErr,
              errors.configErr,
            ]
          : isAluminum || stilesAndRails?.includes("Pico")
            ? [
                errors.widthErr,
                errors.heightErr,
                errors.jambErr,
                errors.configErr,
                errors.withExtSboltErr,
                errors.primaryDoorErr,
                errors.swingWidthErr,
                errors.sideliteWidthErr,
              ]
            : [
                errors.widthErr,
                errors.heightErr,
                errors.jambErr,
                errors.configErr,
                errors.primaryDoorErr,
                errors.swingWidthErr,
                errors.sideliteWidthErr,
              ];

  const requiredArray = useMemo(() => {
    let defaultRequireds = [
      woodSpecies,
      material,
      height,
      unitSize,
      exactConfig,
    ];

    if (isAluminum) {
      defaultRequireds.shift();
    }

    if (isSwing) {
      return [...defaultRequireds, swingPanelWidth];
    }

    if (isPivot) {
      return [...defaultRequireds, swingPanelWidth, pivotHardware, pivotAction];
    }

    if (isCorner) {
      return [...defaultRequireds, rightWidth, cornerConfigRight];
    }

    if (isCombo) {
      return [...defaultRequireds, comboWindowUnitSize, comboWindowConfig];
    }

    return defaultRequireds;
  }, [
    doorType,
    material,
    height,
    unitSize,
    exactConfig,
    woodSpecies,
    swingPanelWidth,
    pivotHardware,
    pivotAction,
    rightWidth,
    cornerConfigRight,
    comboWindowConfig,
    comboWindowUnitSize,
  ]);

  useEffect(() => {
    const requireds = isAluminum
      ? requiredArray
      : [...requiredArray, stilesAndRails];

    setShowPrevBtn(requireds.every((itm) => itm));
  }, [requiredArray, stilesAndRails, material]);

  const btnDisabled = isBtnDisabled(errorArray, requiredArray);

  return {
    btnDisabled,
    showPrevBtn,
    openPreview,
    showPortal,
    setOpenPreview,
    setShowPortal,
    editBtn,
    handleMinimize,
  };
}

export default useBtnSelection;
